import { useRef } from 'react';
import { faArrowUpFromBracket, faSparkle } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import { Button } from '@/components/ui/button';
import { FieldSeparator } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Paragraph } from '@/components/ui/typography';

export type ColorPickerField<K extends string = string> = {
  key: K;
  label: string;
};

export type ColorPaletteSelectorProps<K extends string = string> = {
  fields: readonly ColorPickerField<K>[];
  values: Record<K, string>;
  onChange: (key: K, value: string) => void;
  stickyAdvancedOptions?: boolean;
  onGenerateWithAI?: () => void;
  onCreateFromImage?: () => void;
};

export function ColorPaletteSelector<K extends string = string>({
  fields,
  values,
  onChange,
  stickyAdvancedOptions = false,
  onGenerateWithAI,
  onCreateFromImage,
}: ColorPaletteSelectorProps<K>) {
  const hasAdvancedOptions = Boolean(onGenerateWithAI || onCreateFromImage);
  const inputRefs = useRef<Partial<Record<K, HTMLInputElement | null>>>({});

  const handleColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget;
    onChange(name as K, value);
  };

  const openNativePicker = (event: React.MouseEvent<HTMLInputElement>) => {
    const { name } = event.currentTarget;
    const input = inputRefs.current[name as K];
    if (!input) return;
    event.preventDefault();

    if (typeof input.showPicker === 'function') {
      input.showPicker();
      return;
    }

    input.focus();
    input.click();
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className={stickyAdvancedOptions ? 'min-h-0 flex-1 overflow-y-auto pr-1' : 'space-y-2'}>
        <div className="space-y-2">
          {fields.map(({ key, label }) => (
            <div key={key} className="flex items-center justify-between gap-4">
              <Paragraph className="text-sm leading-5 font-medium text-foreground">
                {label}
              </Paragraph>

              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="relative size-8 overflow-hidden rounded-md border shadow-sm">
                    <div
                      className="absolute inset-0"
                      style={{ backgroundColor: values[key] }}
                      aria-hidden="true"
                    />
                    <input
                      type="color"
                      ref={(node) => {
                        inputRefs.current[key] = node;
                      }}
                      value={values[key]}
                      name={key}
                      onChange={handleColorChange}
                      className="absolute inset-0 cursor-pointer opacity-0"
                      aria-label={`${label} color picker`}
                      onClick={openNativePicker}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right" className="font-mono">
                  {label}: {values[key]}
                </TooltipContent>
              </Tooltip>
            </div>
          ))}
        </div>
      </div>

      {hasAdvancedOptions && (
        <div className={stickyAdvancedOptions ? 'sticky bottom-0 z-10 bg-background' : undefined}>
          <FieldSeparator className="my-4">or</FieldSeparator>

          <div className="flex flex-col justify-center gap-3 px-0 pb-4 sm:flex-row sm:px-8">
            {onCreateFromImage && (
              <Button variant="outline" colorScheme="secondary" onClick={onCreateFromImage}>
                <Icon icon={faArrowUpFromBracket} />
                Create from image
              </Button>
            )}
            {onGenerateWithAI && (
              <Button variant="outline" colorScheme="secondary" onClick={onGenerateWithAI}>
                <Icon icon={faSparkle} className="text-primary" />
                Generate with AI
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
